import { Utils } from '../../../../../common/js/Utils';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { Component, ViewChild } from '@angular/core';
import { AccountCenterSection } from '../utilities/accountCenterSection.component';
import { AccountCenterTable } from '../account-center-table/account-center-table.component';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterTableRow } from '../../../classes/account-center/AccountCenterTableRow';
import { AccountCenterTableCell } from '../../../classes/account-center/AccountCenterTableCell';
import type { CoverpathAnnuity } from '../../../../classes/coverpath_subclasses/CoverpathAnnuity';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';

// Status
const statusRowFunctions = {
  show: function (annuity) {
    return true;
  },
  header: function (annuity) {
    return new AccountCenterTableCell('heading', 'Status');
  },
  data: function (annuity: CoverpathAnnuity, isCanceled?) {
    let status;
    if (isCanceled) {
      status = 'Canceled';
    } else if (this.hasInForcePolicy) {
      status = `${annuity.workflowState}: ${annuity.workflowSubState}`;
    } else {
      status = 'Pending';
    }
    const row = new AccountCenterTableCell('data-status', status);
    row.setCanceled(isCanceled);
    return row;
  },
};

const tableCreateDef = [
  //Premium
  {
    show: function (annuity) {
      return true;
    },
    header: function (annuity) {
      return new AccountCenterTableCell('heading', 'Premium');
    },
    data: function (annuity) {
      return new AccountCenterTableCell('data', Utils.formatIntCurrency(annuity.initialPurchaseAmount));
    },
  },
  //Annuity Option
  {
    show: function (annuity: CoverpathAnnuity) {
      return !annuity.isSV();
    },
    header: function (annuity) {
      return new AccountCenterTableCell('heading', 'Annuity Options');
    },
    data: function (annuity) {
      return new AccountCenterTableCell('data', annuity.refundType);
    },
  },
  //Income Start Date
  {
    show: function (annuity: CoverpathAnnuity) {
      return !annuity.isSV();
    },
    header: function (annuity) {
      return new AccountCenterTableCell('heading', 'Income Start Date');
    },
    data: function (annuity) {
      return new AccountCenterTableCell('data', CoverpathUtils.getUTCDateString(annuity.annuitizationDate));
    },
  },
  //Income Frequency
  {
    show: function (annuity: CoverpathAnnuity) {
      return !annuity.isSV();
    },
    header: function (annuity) {
      return new AccountCenterTableCell('heading', 'Income Frequency');
    },
    data: function (annuity) {
      return new AccountCenterTableCell('data', annuity.distributingFrequency);
    },
  },
  // Guarantee Period
  {
    show: function (annuity) {
      return annuity.isSV();
    },
    header: function (annuity) {
      return new AccountCenterTableCell('heading', 'Guarantee Period');
    },
    data: function (annuity) {
      return new AccountCenterTableCell('data', annuity.guaranteePeriod);
    },
  },
  //Contract Number
  {
    show: function (annuity) {
      return true;
    },
    header: function (annuity) {
      return new AccountCenterTableCell('heading', 'Contract Number');
    },
    data: function (annuity) {
      return new AccountCenterTableCell('data', annuity.contractNumber);
    },
  },
];

@Component({
  selector: 'annuity-details-table',
  moduleId: module.id,
  template: '<account-center-table></account-center-table>',
})
export class AnnuityDetailsTableComponent extends AccountCenterSection {
  @ViewChild(AccountCenterTable, { static: true }) table: AccountCenterTable;
  static title: string = 'Annuity Details';

  constructor(amorphicService: AmorphicService, accountCenterService: AccountCenterService) {
    super(amorphicService, accountCenterService);
  }

  ngOnInit(): void {
    this.subscribeToAccountCenterService();
  }

  processWorkflowAction(workflowAction: string): void {
    if (this.hasInForcePolicy) {
      this.table.title = 'Annuity Details';
    } else {
      this.table.title = 'Application Details';
    }
    this.constructTableForPolicies();
  }

  private async constructTable() {
    const rows: Array<AccountCenterTableRow> = [];
    const annuity = this.accountCenterService.primaryPolicy as CoverpathAnnuity;
    rows.push(
      new AccountCenterTableRow([
        statusRowFunctions.header.call(this, annuity),
        statusRowFunctions.data.call(this, annuity),
      ])
    );
    tableCreateDef.forEach((def) => {
      if (def.show.call(this, annuity)) {
        rows.push(new AccountCenterTableRow([def.header.call(this, annuity), def.data.call(this, annuity)]));
      }
    });
    return rows;
  }

  private async constructTableForPolicies(): Promise<void> {
    this.table.rows = await this.constructTable();
  }
}
